import { FEATURED_LINKS, LANDING_PAGE } from 'constants/ShopYourStore';
import Empty from 'constants/empty';
import { UPPER_FUNNEL_REFINEMENTS } from 'constants/UpperFunnel';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import urlUtils from 'utils/Url';
import localeUtils from 'utils/LanguageLocale';
import Location from 'utils/Location';
import basketConstants from 'constants/Basket';

const { SHOP_STORE_AND_DELIVERY_FLYOUT } = LOCAL_STORAGE;
const { DELIVERY_OPTIONS } = basketConstants;

function excludeShopYourStoreMenuItems(menuItems = Empty.Array) {
    if (!menuItems.length) {
        return menuItems;
    }

    const excludedLinkSids = [FEATURED_LINKS.SHOP_SAME_DAY_SID, FEATURED_LINKS.SHOP_MY_STORE_SID];
    const filteredMenuItems = menuItems.filter(item => {
        const sid = item?.sid || '';

        return !excludedLinkSids.some(excludedSid => sid.startsWith(excludedSid));
    });

    return filteredMenuItems;
}

function transformCardRenderings(services = {}) {
    /*
    OMNX-534: Transform this structure:

    {
        "items": [{ "type": "CardDatasource" }, { "type": "CardDatasource" }],
        "parameters": {},
        "type": "CardRendering"
    }

    Into:

    [
        {
            "datasource": {"type": "CardDatasource"},
            "parameters": {},
            "type": "CardRendering"
        },
        {
            "datasource": {"type": "CardDatasource"},
            "parameters": {},
            "type": "CardRendering"
        }
    ]
    */

    const { items, parameters = {}, type } = services;

    if (!Array.isArray(items) || items.length === 0) {
        return null;
    }

    const result = items.map(item => {
        return {
            type,
            parameters,
            datasource: item
        };
    });

    return result;
}

function updateCachedSLAInformation(target, details) {
    try {
        const cache = Storage.session.getItem(SHOP_STORE_AND_DELIVERY_FLYOUT);

        if (cache) {
            let nextCache;

            if (target === LANDING_PAGE.STORE_DETAILS && cache?.storeDetails) {
                nextCache = {
                    ...cache,
                    storeDetails: {
                        ...cache.storeDetails,
                        pickupMessage: details?.pickupMessage
                    }
                };
            } else if (target === LANDING_PAGE.ZIP_CODE_DETAILS && cache?.sameDay) {
                nextCache = {
                    ...cache,
                    sameDay: {
                        ...cache.sameDay,
                        deliveryMessage: details?.deliveryMessage
                    }
                };
            }

            if (nextCache) {
                Storage.session.setItem(SHOP_STORE_AND_DELIVERY_FLYOUT, nextCache, Storage.MINUTES * 15);
            }
        }
    } catch (error) {
        Sephora.logger.error('Failed to update SLA information in cache:', error);
    }
}

function shopYourStoreEnabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'isShopYourStoreEnabled' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName] === true;

    return isEnabled;
}

function shopYourStoreV2Enabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'shopYourStoreV2' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName]?.isEnabled === true;

    return isEnabled;
}

function shopYourStoreNewBestEnabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'shopYourStoreNewAndBest' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName]?.isEnabled === true;

    return isEnabled;
}

function shopYourStoreFilterEnabled() {
    const country = localeUtils.getCurrentCountry();
    const killswitchName = 'shopYourStoreFilter' + country;
    const isEnabled = Sephora.configurationSettings[killswitchName]?.isEnabled === true;

    return isEnabled;
}

function persistRefinementsInUrl(url) {
    const isShopYourStoreV2Enabled = shopYourStoreV2Enabled();

    if (!url || !isShopYourStoreV2Enabled) {
        return url;
    }

    const PERSIST_REF_PARAMS = [...UPPER_FUNNEL_REFINEMENTS];
    const ref = urlUtils.getParams()?.ref || Empty.Array;

    const fulfillmentRefinements = ref.filter(r => {
        const key = r.split('=')[0];

        return PERSIST_REF_PARAMS.includes(key);
    });

    if (fulfillmentRefinements.length) {
        const refParam = fulfillmentRefinements.join(',');

        if (!url.includes('ref')) {
            const CONCAT_CHAR = !url.includes('?') ? '?' : '&';
            const urlWithRefinements = `${url}${CONCAT_CHAR}ref=${refParam}`;

            return urlWithRefinements;
        }
    }

    return url;
}

function getInitialDeliveryOption(killswitch) {
    const isShopYourStoreV2Enabled = killswitch ?? shopYourStoreV2Enabled();

    if (!isShopYourStoreV2Enabled) {
        return null;
    }

    if (Location.isShopMyStorePage()) {
        return DELIVERY_OPTIONS.PICKUP;
    } else if (Location.isShopSameDayPage()) {
        return DELIVERY_OPTIONS.SAME_DAY;
    }

    return null;
}

function mergeElegibilityFlags(items = [], eligibilityFlags = {}) {
    const itemsWithEligibilityFlags = [];

    for (const item of items) {
        const eligibility = eligibilityFlags[item?.productId] || Empty.Object;
        itemsWithEligibilityFlags.push({
            ...item,
            ...eligibility
        });
    }

    return itemsWithEligibilityFlags;
}

export default {
    excludeShopYourStoreMenuItems,
    transformCardRenderings,
    updateCachedSLAInformation,
    shopYourStoreNewBestEnabled,
    persistRefinementsInUrl,
    mergeElegibilityFlags,
    shopYourStoreEnabled,
    shopYourStoreV2Enabled,
    shopYourStoreFilterEnabled,
    getInitialDeliveryOption
};
